import React, { useState, useEffect } from 'react';
import {
  Card,
  Image,
  Spin,
  Empty,
  Typography,
  Space,
  Button,
  message,
  Collapse,
  Tag,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface HotelImageGalleryProps {
  hotelId: number;
}

interface ImageData {
  ImageId: string;
  CategoryId: string;
  CategoryName: string;
  Url: string;
  Type: string;
}

const HotelImageGallery: React.FC<HotelImageGalleryProps> = ({ hotelId }) => {
  const [data, setData] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupedData, setGroupedData] = useState<Record<string, ImageData[]>>({});
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (hotelId) {
      fetchData();
    }
  }, [hotelId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/images`);
      const fetchedData = response.data;
      setData(fetchedData);

      // Group by category name (room type)
      const grouped = fetchedData.reduce((acc: Record<string, ImageData[]>, item: ImageData) => {
        const category = item.CategoryName || 'Uncategorized';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {});

      setGroupedData(grouped);
    } catch (err) {
      message.error('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      message.success('Image downloaded successfully!');
    } catch (err) {
      message.error('Failed to download image');
      console.error(err);
    }
  };

  const downloadCategoryImages = async (categoryName: string) => {
    setDownloading(categoryName);
    const images = groupedData[categoryName];

    try {
      message.loading(`Downloading ${images.length} images...`, 0);

      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const filename = `${categoryName.replace(/\s+/g, '_')}_${i + 1}_${img.ImageId.substring(0, 8)}.jpg`;
        await downloadImage(img.Url, filename);

        // Small delay to avoid overwhelming the browser
        if (i < images.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      message.destroy();
      message.success(`All ${images.length} images downloaded!`);
    } catch (err) {
      message.destroy();
      message.error('Failed to download some images');
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const downloadAllImages = async () => {
    setDownloading('all');

    try {
      message.loading(`Downloading ${data.length} images...`, 0);

      let index = 0;
      for (const category of Object.keys(groupedData)) {
        const images = groupedData[category];
        for (const img of images) {
          index++;
          const filename = `${category.replace(/\s+/g, '_')}_${img.ImageId.substring(0, 8)}.jpg`;
          await downloadImage(img.Url, filename);

          // Small delay to avoid overwhelming the browser
          if (index < data.length) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
      }

      message.destroy();
      message.success(`All ${data.length} images downloaded!`);
    } catch (err) {
      message.destroy();
      message.error('Failed to download some images');
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading images..." />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No images found"
        >
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
        </Empty>
      </div>
    );
  }

  const categories = Object.keys(groupedData).sort();

  return (
    <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              <PictureOutlined /> Room Type Images
            </Title>
            <Text type="secondary">
              {data.length} images in {categories.length} room types
            </Text>
          </div>

          <Space direction="vertical" align="end">
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchData}
                disabled={downloading !== null}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadAllImages}
                loading={downloading === 'all'}
                disabled={downloading !== null}
              >
                {downloading === 'all' ? 'Downloading...' : 'Download All Images'}
              </Button>
            </Space>
          </Space>
        </div>

        <Collapse
          defaultActiveKey={categories}
          style={{ background: 'transparent', border: 'none' }}
        >
          {categories.map((category) => {
            const images = groupedData[category];
            const isDownloading = downloading === category;

            return (
              <Panel
                header={
                  <Space>
                    <Text strong>{category}</Text>
                    <Tag color="blue">{images.length} images</Tag>
                  </Space>
                }
                key={category}
                extra={
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadCategoryImages(category);
                    }}
                    loading={isDownloading}
                    disabled={downloading !== null}
                  >
                    Download All
                  </Button>
                }
                style={{
                  marginBottom: 8,
                  background: '#fff',
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 12,
                  padding: 8,
                }}>
                  {images.map((img) => (
                    <Card
                      key={img.ImageId}
                      hoverable
                      cover={
                        <Image
                          src={img.Url}
                          alt={`${category} - ${img.ImageId}`}
                          style={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                          }}
                          preview={{
                            src: img.Url,
                          }}
                        />
                      }
                      size="small"
                      actions={[
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          size="small"
                          onClick={() => downloadImage(img.Url, `${category.replace(/\s+/g, '_')}_${img.ImageId.substring(0, 8)}.jpg`)}
                        >
                          Download
                        </Button>
                      ]}
                    >
                      <Card.Meta
                        description={
                          <Text
                            type="secondary"
                            style={{ fontSize: 11 }}
                            ellipsis
                          >
                            ID: {img.ImageId.substring(0, 8)}...
                          </Text>
                        }
                      />
                    </Card>
                  ))}
                </div>
              </Panel>
            );
          })}
        </Collapse>
    </div>
  );
};

export default HotelImageGallery;
